import { useState, type FormEvent } from 'react';
import { z } from 'zod';
import { Input, Select, Field } from '@/components/ui/Field';
import { Checkbox } from '@/components/ui/Checkbox';
import { Button } from '@/components/ui/Button';
import { MediaField } from '@/components/forms/MediaField';
import { MarkdownField } from '@/components/forms/MarkdownField';
import { slugify } from '@/lib/utils';
import { useAdminInfluencers, type ContentRow } from '@/lib/admin-queries';

const CATEGORIES = ['short-drama', 'micro-series', 'promo', 'behind-the-scenes'] as const;
const PUBLISH = ['draft', 'published', 'archived'] as const;

interface Props {
  value: ContentRow;
  submitting: boolean;
  onSubmit: (v: ContentRow) => void | Promise<void>;
  onCancel: () => void;
}

const contentSchema = z.object({
  slug: z.string().regex(/^[a-z0-9-]+$/, 'Lowercase letters, numbers, and hyphens only.'),
  title: z.string().min(2, 'Title is required.'),
  summary: z.string().min(10, 'Add a short summary (≥ 10 chars).').max(280, 'Keep it under 280 chars.'),
  category: z.enum(CATEGORIES),
  status: z.enum(PUBLISH),
});

function isValidUrl(s: string | null | undefined): boolean {
  if (!s) return true;
  try {
    const u = new URL(s);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

export function ContentEditForm({ value, submitting, onSubmit, onCancel }: Props) {
  const { data: influencers = [] } = useAdminInfluencers();
  const [v, setV] = useState<ContentRow>(value);
  const [errors, setErrors] = useState<Record<string, string>>({});

  function patch<K extends keyof ContentRow>(key: K, val: ContentRow[K]) {
    setV((cur) => ({ ...cur, [key]: val }));
  }

  function onTitleBlur() {
    if (!v.slug) patch('slug', slugify(v.title));
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErrors({});
    const next: Record<string, string> = {};
    const parsed = contentSchema.safeParse(v);
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        const key = issue.path[0];
        if (typeof key === 'string' && !next[key]) next[key] = issue.message;
      }
    }
    for (const f of ['tiktok_url', 'instagram_url', 'youtube_url', 'x_url'] as const) {
      if (!isValidUrl(v[f])) next[f] = 'Must be a valid http(s) URL.';
    }
    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }
    onSubmit(v);
  }

  return (
    <form id="content-form" onSubmit={handleSubmit} className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2">
        <Field id="title" label="Title" required error={errors.title}>
          {({ id, invalid }) => (
            <Input
              id={id}
              required
              invalid={invalid}
              value={v.title}
              onChange={(e) => patch('title', e.target.value)}
              onBlur={onTitleBlur}
            />
          )}
        </Field>
        <Field id="slug" label="Slug" required error={errors.slug} hint="Lowercase letters, numbers, hyphens.">
          {({ id, invalid }) => (
            <Input
              id={id}
              required
              invalid={invalid}
              value={v.slug}
              onChange={(e) => patch('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
            />
          )}
        </Field>
      </div>

      <Field id="summary" label="Summary" required error={errors.summary} hint="Shown on the card and the detail page lede. ≤ 280 chars.">
        {({ id, invalid }) => (
          <Input
            id={id}
            required
            invalid={invalid}
            maxLength={280}
            value={v.summary}
            onChange={(e) => patch('summary', e.target.value)}
          />
        )}
      </Field>

      <div className="grid gap-4 md:grid-cols-4">
        <Field id="category" label="Category" required>
          {({ id }) => (
            <Select
              id={id}
              value={v.category}
              onChange={(e) => patch('category', e.target.value as ContentRow['category'])}
            >
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </Select>
          )}
        </Field>
        <Field id="status" label="Publish" required>
          {({ id }) => (
            <Select
              id={id}
              value={v.status}
              onChange={(e) => patch('status', e.target.value as ContentRow['status'])}
            >
              {PUBLISH.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </Select>
          )}
        </Field>
        <Field id="influencer" label="Primary influencer">
          {({ id }) => (
            <Select
              id={id}
              value={v.primary_influencer_id ?? ''}
              onChange={(e) => patch('primary_influencer_id', e.target.value || null)}
            >
              <option value="">— None —</option>
              {influencers
                .filter((i) => i.id)
                .map((i) => (
                  <option key={i.id} value={i.id}>{i.name}</option>
                ))}
            </Select>
          )}
        </Field>
        <Field id="display_order" label="Order">
          {({ id }) => (
            <Input
              id={id}
              type="number"
              value={v.display_order}
              onChange={(e) => patch('display_order', Number(e.target.value))}
            />
          )}
        </Field>
      </div>

      <Field id="cover" label="Cover image" hint="16:9 image · used on the card and the detail page hero.">
        <MediaField value={v.cover_media_id} onChange={(m) => patch('cover_media_id', m)} bucket="public" />
      </Field>

      <Field id="description_md" label="Full description" hint="Long-form for the detail page.">
        <MarkdownField value={v.description_md ?? ''} onChange={(s) => patch('description_md', s)} rows={14} />
      </Field>

      <div className="grid gap-4 md:grid-cols-2">
        <Field id="tiktok_url" label="TikTok URL" error={errors.tiktok_url}>
          {({ id, invalid }) => (
            <Input
              id={id}
              type="url"
              invalid={invalid}
              value={v.tiktok_url ?? ''}
              onChange={(e) => patch('tiktok_url', e.target.value || null)}
              placeholder="https://tiktok.com/@series"
            />
          )}
        </Field>
        <Field id="instagram_url" label="Instagram URL" error={errors.instagram_url}>
          {({ id, invalid }) => (
            <Input
              id={id}
              type="url"
              invalid={invalid}
              value={v.instagram_url ?? ''}
              onChange={(e) => patch('instagram_url', e.target.value || null)}
              placeholder="https://instagram.com/series"
            />
          )}
        </Field>
        <Field id="youtube_url" label="YouTube URL" error={errors.youtube_url}>
          {({ id, invalid }) => (
            <Input
              id={id}
              type="url"
              invalid={invalid}
              value={v.youtube_url ?? ''}
              onChange={(e) => patch('youtube_url', e.target.value || null)}
              placeholder="https://youtube.com/playlist?list=..."
            />
          )}
        </Field>
        <Field id="x_url" label="X (Twitter) URL" error={errors.x_url}>
          {({ id, invalid }) => (
            <Input
              id={id}
              type="url"
              invalid={invalid}
              value={v.x_url ?? ''}
              onChange={(e) => patch('x_url', e.target.value || null)}
              placeholder="https://x.com/series"
            />
          )}
        </Field>
      </div>

      <label className="flex items-center gap-2 text-sm">
        <Checkbox
          checked={v.is_featured}
          onCheckedChange={(c) => patch('is_featured', !!c)}
          id="is_featured"
        />
        <span>Feature on the Contents page</span>
      </label>

      <div className="flex items-center justify-end gap-2 border-t border-border pt-4">
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" form="content-form" loading={submitting}>
          {v.id ? 'Save changes' : 'Create drama'}
        </Button>
      </div>
    </form>
  );
}
